/**
 * Converts a lowercase Provider key to its display-cased CloudProvider form.
 *
 * Use for a typed `CloudProvider!` GraphQL argument, a `ProviderIcon` prop, or
 * human-readable text. Do NOT use for a filter value, a relay `@connection`
 * cache key, or anything else compared against the raw key -- pass `provider`
 * directly there. For every provider but Kubernetes this only changes case,
 * so a case-insensitive comparison downstream can mask the misuse; "k8s" ->
 * "Kubernetes" is a different word, so that misuse breaks outright.
 *
 * @param provider - The lowercase Provider enum value to convert.
 * @returns The corresponding uppercase CloudProvider enum value.
 *
 * @example
 * ```typescript
 * formatProvider("aws") // Returns "AWS"
 * formatProvider("gcp") // Returns "GCP"
 * formatProvider("k8s") // Returns "Kubernetes"
 * formatProvider("unknown") // Returns "AWS" (default fallback)
 * ```
 */

export type CloudProvider =
  | "AWS"
  | "Azure"
  | "GCP"
  | "TencentCloud"
  | "Kubernetes"
  | "%future added value";

export const DEFAULT_PROVIDERS = ["aws", "azure", "gcp", "tencentcloud", "k8s"] as const;

export type Provider = (typeof DEFAULT_PROVIDERS)[number] | "%future added value";

// Typeguard to check if a value is a valid Provider
export function isProvider(value: string | Provider): value is Provider {
  return typeof value === "string" && (DEFAULT_PROVIDERS as readonly string[]).includes(value);
}

export function formatProvider(provider: Provider): CloudProvider {
  switch (provider) {
    case "aws":
      return "AWS";
    case "azure":
      return "Azure";
    case "gcp":
      return "GCP";
    case "tencentcloud":
      return "TencentCloud";
    case "k8s":
      return "Kubernetes";
    default:
      return "AWS";
  }
}

export type CloudProviderFullName =
  | "Amazon Web Services"
  | "Microsoft Azure"
  | "Google Cloud Platform"
  | "Tencent Cloud"
  | "Kubernetes"
  | "%future added value";

export function formatProviderFullName(provider: Provider): CloudProviderFullName {
  switch (provider) {
    case "aws":
      return "Amazon Web Services";
    case "azure":
      return "Microsoft Azure";
    case "gcp":
      return "Google Cloud Platform";
    case "tencentcloud":
      return "Tencent Cloud";
    case "k8s":
      return "Kubernetes";
    default:
      return "Amazon Web Services";
  }
}

export type ActionNoun = "account" | "cluster";

export type IdentifierNoun = "account" | "subscription" | "project" | "cluster";

export interface ProviderVocabulary {
  /** What you Add/Edit/Remove. "account" for every provider but Kubernetes. */
  actionNoun: ActionNoun;
  /** What the specific identifier value is called (Name-field hint,
   * details-page ID label). Matches each provider's own AccountId field
   * label exactly. */
  identifierNoun: IdentifierNoun;
  /** Overrides the derived "<capitalized identifier noun> ID" details-page
   * label, for providers whose field isn't called an "ID" at all. */
  identifierLabel?: string;
  /** Whether the Name field shows a "<article> <identifier noun> name" hint.
   * False for AWS, where the account key itself is the identifier and the
   * hint would be redundant. */
  showsIdentifierHint: boolean;
}

const DEFAULT_VOCABULARY: ProviderVocabulary = {
  actionNoun: "account",
  identifierNoun: "account",
  showsIdentifierHint: true,
};

// Sparse by design: only providers that differ from DEFAULT_VOCABULARY need an entry here.
const VOCABULARY_OVERRIDES: Partial<Record<CloudProvider, Partial<ProviderVocabulary>>> = {
  AWS: { showsIdentifierHint: false },
  Azure: { identifierNoun: "subscription" },
  GCP: { identifierNoun: "project" },
  Kubernetes: {
    actionNoun: "cluster",
    identifierNoun: "cluster",
    identifierLabel: "Cluster Key",
  },
};

export function getProviderVocabulary(provider: CloudProvider): ProviderVocabulary {
  return { ...DEFAULT_VOCABULARY, ...VOCABULARY_OVERRIDES[provider] };
}

export interface AccountNoun {
  singular: ActionNoun;
  singularTitle: Capitalize<ActionNoun>;
  plural: `${ActionNoun}s`;
  pluralTitle: Capitalize<`${ActionNoun}s`>;
}

// Only uppercases the first character -- fine for the single-word nouns in
// play today, wrong for a future multi-word noun (e.g. "resource group").
export function capitalize<T extends string>(word: T): Capitalize<T> {
  return (word.charAt(0).toUpperCase() + word.slice(1)) as Capitalize<T>;
}

function accountNoun(singular: ActionNoun): AccountNoun {
  const plural: `${ActionNoun}s` = `${singular}s`;
  return {
    singular,
    singularTitle: capitalize(singular),
    plural,
    pluralTitle: capitalize(plural),
  };
}

export function getAccountNoun(provider: CloudProvider): AccountNoun {
  return accountNoun(getProviderVocabulary(provider).actionNoun);
}

export function getIdentifierNoun(provider: CloudProvider): IdentifierNoun {
  return getProviderVocabulary(provider).identifierNoun;
}

export function getIdentifierLabel(provider: CloudProvider): string {
  const vocabulary = getProviderVocabulary(provider);
  return vocabulary.identifierLabel ?? `${capitalize(vocabulary.identifierNoun)} ID`;
}

// "account" is the only noun in play (across both actionNoun and
// identifierNoun) that starts with a vowel sound -- not a general English
// article rule, just correct for this specific, closed set of words.
const VOWEL_SOUND_NOUNS = new Set<IdentifierNoun>(["account"]);

export function indefiniteArticle(noun: IdentifierNoun): "a" | "an" {
  return VOWEL_SOUND_NOUNS.has(noun) ? "an" : "a";
}

export function getNameFieldHint(provider: CloudProvider): string | undefined {
  const vocabulary = getProviderVocabulary(provider);
  if (!vocabulary.showsIdentifierHint) {
    return undefined;
  }
  const noun = vocabulary.identifierNoun;
  return `${capitalize(indefiniteArticle(noun))} ${noun} name`;
}
